import React, { useEffect, useRef, useState, type JSX } from 'react';
import {
  Checkbox as PrimeCheckbox,
  type CheckboxChangeEvent,
  type CheckboxProps as PrimeCheckboxProps,
} from 'primereact/checkbox';
import styles from './Checkbox.module.scss';

/**
 * CheckboxSize defines the size of the checkbox.
 */
export type CheckboxSize = 'Small' | 'Medium';

/**
 * CheckboxState defines the state of the checkbox.
 */
export type CheckboxState = 'default' | 'partial' | 'selected' | 'hover';

/**
 * Props for the Custom Checkbox component.
 */
export interface CustomCheckboxProps {
  isDisable?: boolean;
  isLabel?: boolean;
  size?: CheckboxSize;
  state?: CheckboxState;
  label?: string;
  checkboxId: string;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  checkboxProps?: Partial<PrimeCheckboxProps>;
}

/**
 * Custom Checkbox component using PrimeReact's Checkbox with support for size, label, disabled state, and visual states.
 *
 * ## Import path
 * ```tsx
 * import { Checkbox } from '@kpmg-global-audit-emu/react-ui-kit/atoms/Checkbox';
 * ```
 * @param props - The props for the checkbox component.
 * @param props.isDisable - Whether the checkbox is disabled.
 * @param props.isLabel - Whether to show a label next to the checkbox.
 * @param props.size - Size of the checkbox.
 * @param props.state - Visual state of the checkbox.
 * @param props.label - Optional label text.
 * @param props.checkboxId - Unique identifier for the checkbox.
 * @param props.checked - Controlled checked state of the checkbox.
 * @param props.onChange - Callback function when the checked state changes.
 * @param props.checkboxProps - Additional props to pass to the underlying PrimeReact Checkbox component.
 * @returns JSX.Element A styled checkbox component.
 */
export const Checkbox: React.FC<Readonly<CustomCheckboxProps>> = ({
  isDisable = false,
  isLabel = true,
  size = 'Medium',
  state = 'default',
  label = '',
  checkboxId,
  checked: controlledChecked,
  onChange,
  checkboxProps,
}): JSX.Element => {
  const checkboxRef = useRef<HTMLInputElement>(null);
  const [uncontrolledChecked, setUncontrolledChecked] = useState(false);

  const isControlled = controlledChecked !== undefined;
  const checked = isControlled ? controlledChecked : uncontrolledChecked;

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = state === 'partial';
    }

    // Storybook visual states
    if (!isControlled) {
      if (state === 'selected') setUncontrolledChecked(true);
      if (state === 'default') setUncontrolledChecked(false);
    }
  }, [state, isControlled]);

  const handleChange = (event: CheckboxChangeEvent) => {
    const value = !!event.checked;

    if (!isControlled) {
      setUncontrolledChecked(value);
    }

    onChange?.(value);
  };

  const colorClass = isDisable ? styles['checkbox-default'] : styles['checkbox-color'];
  const sizeClass = size === 'Small' ? styles['checkbox-small'] : styles['checkbox-medium'];
  const disabledClass = isDisable ? styles['text-disabled'] : '';

  return (
    <div
      className={`${styles['custom-checkbox']} ${sizeClass} ${colorClass} ${disabledClass} ${styles[state?.toLowerCase()]}`}
    >
      <PrimeCheckbox
        {...checkboxProps}
        inputId={checkboxId}
        checked={checked}
        onChange={handleChange}
        disabled={isDisable}
        inputRef={checkboxRef}
        className={[styles.checkbox, checkboxProps?.className].filter(Boolean).join(' ')}
      />
      {isLabel && (
        <label htmlFor={checkboxId} className={`${styles['checkbox-label']} ${disabledClass}`}>
          {label === '' ? 'Text Label' : label}
        </label>
      )}
    </div>
  );
};
